import React from 'react';
import type { AggregatedPortfolioSettings, FrequencyEnum } from 'venn-api';
import type { AsOfDateSetting } from 'venn-utils';
import { CURRENT_AS_OF_DATE } from 'venn-utils';
import type { HistoricalNodeTaggedWithRange } from './types';

export type HistoricalPortfolioCommon = {
  allocationPanelType: 'historical read-only';
  allocationsAsOfDate: AsOfDateSetting;
  allocationsAsOfDateTimestamp: number | undefined;
  setAllocationsAsOfDate: (asOfDate: AsOfDateSetting) => void;
  isAllocationsForwardFilled: boolean;
};
export type HistoricalPortfolioData = {
  portfolio: HistoricalNodeTaggedWithRange;
  allocationDates: number[];
  lastUploadedAllocationsDate: number;
  currentAsOfDate: number;
  frequency: FrequencyEnum;
};
export type SuccessHistoricalPortfolioContextValue = HistoricalPortfolioCommon &
  HistoricalPortfolioData & {
    status: 'success';
  };
type LoadingHistoricalPortfolioContextValue = HistoricalPortfolioCommon & {
  status: 'loading';
};
type ErrorHistoricalPortfolioContextValue = HistoricalPortfolioCommon & {
  status: 'error';
  errorType: 'insufficient overlap with returns' | undefined;
};
export type HistoricalPortfolioContextValue =
  | SuccessHistoricalPortfolioContextValue
  | LoadingHistoricalPortfolioContextValue
  | ErrorHistoricalPortfolioContextValue;

type ProFormaPortfolioContextValue = {
  allocationPanelType: 'full';
  isLeverageShortPortfolio: boolean;
  isPortfolioLabDisabled: boolean;
  /** Present only for aggregated portfolios, which are read-only and can be re-saved as live. */
  aggregatedPortfolioSettings?: AggregatedPortfolioSettings;
};

export type AllocationPanelContextValue = HistoricalPortfolioContextValue | ProFormaPortfolioContextValue;

export const defaultProFormaPortfolioContextValue: AllocationPanelContextValue = {
  allocationPanelType: 'full',
  isLeverageShortPortfolio: false,
  isPortfolioLabDisabled: false,
};

export const defaultHistoricalPortfolioContextValue: AllocationPanelContextValue = {
  allocationPanelType: 'historical read-only',
  status: 'error',
  errorType: undefined,
  allocationsAsOfDate: CURRENT_AS_OF_DATE,
  allocationsAsOfDateTimestamp: undefined,
  setAllocationsAsOfDate: () => {},
  isAllocationsForwardFilled: false,
};

export const AllocationPanelContext = React.createContext<AllocationPanelContextValue>(
  defaultProFormaPortfolioContextValue,
);
AllocationPanelContext.displayName = 'AllocationPanelContext';
